import { css, html, LitEntity, renderIf, SirenActionMixin } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-loading';
import './import-mapping-constructor';
import './import-mapping-view';
import './import-preview';

class ImportMappingSelector extends SirenActionMixin(LitEntity) {
  static get properties() {
    return {
      availableHeaders: { type: Array },
      recordPreview: { type: Object },
      targetGroupGenerator: { type: Function },
      importPreviewRenderer: { type: Function },
      currentView: { type: String, attribute: false },
      selectingMapping: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .mapping-list {
        margin-top: 1em;
      }

      .mapping-list > * {
        margin-left: 0;
        margin-right: 0;
      }

      bn-surface[selected] {
        background-color: var(--blue-20);
        border: 1px solid var(--blue-60);
      }

      .floating {
        float: right;
        margin-left: 0.5em;
      }

      .constructor-cancel {
        margin: 1em;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html`
        <bn-loading></bn-loading>
      `;
    }
    const view = this.currentView ||
      (this.currentSelectionHref ? 'view' : this.assignableItems.length > 0 ? 'selector' : 'constructor');

    return html`
      ${renderIf(view === 'view', () => html`
        <button class="button button--outlined floating" @click="${() => this.currentView = 'selector'}">Change Mapping</button>
        <import-mapping-view
          .href="${this.currentSelectionHref}"
          .availableHeaders="${this.availableHeaders}"
          .recordPreview="${this.recordPreview}"
          .targetGroupGenerator="${this.targetGroupGenerator}"
          .importPreviewRenderer="${this.importPreviewRenderer}"
        ></import-mapping-view>
      `)}
      ${renderIf(view === 'constructor' && this.beginCreateMappingAction, () => html`
        ${renderIf(this.currentSelectionHref || this.assignableItems.length > 0, () => html`
          <button class="button button--outlined floating constructor-cancel" @click="${() => this.currentView = 'selector'}">Cancel
          </button>
        `)}
        <import-mapping-constructor
          .href="${this.beginCreateMappingAction.href}"
          .targetGroupGenerator="${this.targetGroupGenerator}"
          @created="${this._onMappingCreated}"
        ></import-mapping-constructor>
      `)}
      ${renderIf(view === 'selector', () => html`
        ${renderIf(this.currentSelectionHref, () => html`
          <button class="button button--outlined floating" @click="${() => this.currentView = 'view'}">Cancel</button>
        `)}
        <button class="button button--contained floating" @click=${() => this.currentView = 'constructor'}>
          <bn-icon name="fal fa-plus"></bn-icon>
          Create New Mapping
        </button>
        <h2>Select Mapping</h2>
        <div class="mapping-list">
          ${this.allItems.map(this._renderMapping.bind(this))}
        </div>
      `)}
    `;
  }

  _renderMapping(mappingEntity) {
    const { mapping, createdAt, lastImportedAt, lastImportName } = mappingEntity.properties;

    const importPreview = html`
        <import-preview
          show-usage
          .createdAt="${createdAt}"
          .lastImportedAt="${lastImportedAt}"
          .lastImportName="${lastImportName}"
          .recordPreview="${this.recordPreview}"
          .mapping="${mapping}"
          .importPreviewRenderer="${this.importPreviewRenderer}"
        ></import-preview>
    `;

    if (this.getHref('via', mappingEntity) === this.currentSelectionHref) {
      return html`
        <bn-surface selected>
          ${importPreview}
        </bn-surface>
      `;
    }
    return html`
      <bn-card @click=${() => this._selectMapping(mappingEntity)}>
        ${importPreview}
      </bn-card>
    `;
  }

  async _selectMapping(mapping) {
    if (this.selectingMapping) {
      return;
    }
    this.selectingMapping = true;
    this.currentView = 'view';
    await this.submitAction(mapping.getAction('assign'));
  }

  _onMappingCreated() {
    this.currentView = 'view';
    this.dispatchEvent(new CustomEvent('mapping-created'));
  }

  update(changedProperties) {
    super.update(changedProperties);
    if (changedProperties.has('currentView')) {
      this.dispatchEvent(new CustomEvent('view-change', { detail: { view: this.currentView }, bubbles: true, composed: true }));
    }
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity) {
      return;
    }
    this.selectingMapping = false;
    this.beginCreateMappingAction = this.entity.getActionByName('begin-create-mapping');
    this.currentSelectionHref = this.getHref('via', this.entity.getSubEntityByClass('current-selection'));
    this.assignableItems = this.entity.getSubEntitiesByClass('assignable-item');
    this.allItems = this.entity.getSubEntitiesByClass('import-mapping-view');
  }
}

window.customElements.define('import-mapping-selector', ImportMappingSelector);
